import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {
  Notification,
  NotificationsPaginatedResponse,
} from '@gatewee/types/notification';
import {TaskScope} from '@gatewee/types/task';
import {Observable} from 'rxjs';
import {SKIP_SPINNER} from './tokens/spinner.token';

export interface NotificationUnreadCountResponse {
  unreadCount: number;
}

export interface NotificationReadAllResponse {
  updated: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);

  findAllPaginated(
    scope: TaskScope,
    skip: number,
    take: number,
    options?: Record<string, unknown>,
    skipSpinner = false,
  ): Observable<NotificationsPaginatedResponse> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('take', take.toString());

    if (options && Object.keys(options).length > 0) {
      Object.entries(options).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }
        const serialized =
          typeof value === 'object' ? JSON.stringify(value) : String(value);
        params = params.append(`options[${key}]`, serialized);
      });
    }

    return this.http.get<NotificationsPaginatedResponse>(
      `/notifications/${scope}/paginated`,
      {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      },
    );
  }

  getUnreadCount(
    scope: TaskScope,
    skipSpinner = false,
  ): Observable<NotificationUnreadCountResponse> {
    return this.http.get<NotificationUnreadCountResponse>(
      `/notifications/${scope}/unread-count`,
      {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      },
    );
  }

  findOne(
    scope: TaskScope,
    id: number,
    skipSpinner = false,
  ): Observable<Notification> {
    return this.http.get<Notification>(`/notifications/${scope}/${id}`, {
      context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
    });
  }

  markAsRead(
    scope: TaskScope,
    id: number,
    skipSpinner = false,
  ): Observable<Notification> {
    return this.http.patch<Notification>(
      `/notifications/${scope}/${id}/read`,
      {},
      {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      },
    );
  }

  markAllAsRead(
    scope: TaskScope,
    skipSpinner = false,
  ): Observable<NotificationReadAllResponse> {
    return this.http.patch<NotificationReadAllResponse>(
      `/notifications/${scope}/read-all`,
      {},
      {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      },
    );
  }
}
